import { appUrl } from "@/utils/app-url";
import type { NextResponse } from "next/server";
import type { RouteDefinitions } from "./$route-client";
import type { routeHandlerResponseBrand } from "./route-handler-response";

export type HttpMethod = "GET" | "HEAD" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * routeの定義（src/utils/$route-client.ts に生成される）
 *
 * - handlers: route.ts のモジュールの型
 * - params: route.ts でexportされたパスパラメータの型（`Params`）
 * - inputs: route.ts でexportされたメソッドごとのクエリ（`GETQuery` など）とボディ（`POSTBody` など）の型
 */
export type RouteDefinitionMap = Record<
  string,
  {
    handlers: object;
    params?: object;
    inputs: Partial<Record<HttpMethod, { Query?: unknown; Body?: unknown }>>;
  }
>;

/**
 * routeで利用可能なメソッド（route.ts でexportされているハンドラーのメソッド）
 */
export type RouteMethod<
  Defs extends RouteDefinitionMap,
  P extends keyof Defs,
> = Extract<keyof Defs[P]["handlers"], HttpMethod>;

// 指定したメソッドを持つrouteのパス
export type RoutePathnameFor<
  Defs extends RouteDefinitionMap,
  M extends HttpMethod,
> = {
  [P in keyof Defs & string]: M extends RouteMethod<Defs, P> ? P : never;
}[keyof Defs & string];

type ParamNames<P extends string> =
  P extends `${string}[${infer K}]${infer Rest}` ? K | ParamNames<Rest> : never;

type QueryValue = string | number | boolean | undefined | null;
type Query = Record<string, QueryValue | QueryValue[]>;

// NextResponse.json() で返している値の型を取り出す
// new Response() や型の分からない NextResponse<unknown> は除外する
type JsonBodyOf<R> =
  R extends NextResponse<infer B> ? (unknown extends B ? never : B) : never;
// 戻り値の型が RouteHandlerResponse で宣言されているか
type IsDeclaredResponse<R> = true extends (
  R extends unknown
    ? typeof routeHandlerResponseBrand extends keyof R
      ? true
      : false
    : never
)
  ? true
  : false;

/**
 * ハンドラーが返すJSONの型
 *
 * - 戻り値が RouteHandlerResponse で宣言されている場合はそのJSONの型（JSONを返さない場合は never）
 * - 宣言されていない場合は推論された型から取り出す
 *   ただし new Response() や NextResponse.redirect() などが混在すると、TypeScriptの推論で戻り値の型が
 *   Response / NextResponse<unknown> に丸められるため unknown になる
 *
 * @knipignore
 */
export type ResponseJson<H> = H extends (...args: never) => infer R
  ? IsDeclaredResponse<Awaited<R>> extends true
    ? JsonBodyOf<Awaited<R>>
    : [JsonBodyOf<Awaited<R>>] extends [never]
      ? unknown
      : JsonBodyOf<Awaited<R>>
  : unknown;

export type RouteResponseJson<
  Defs extends RouteDefinitionMap,
  P extends keyof Defs,
  M extends HttpMethod,
> = M extends keyof Defs[P]["handlers"]
  ? ResponseJson<Defs[P]["handlers"][M]>
  : unknown;

type MethodInput<
  Defs extends RouteDefinitionMap,
  P extends keyof Defs,
  M extends HttpMethod,
> = M extends keyof Defs[P]["inputs"] ? Defs[P]["inputs"][M] : {};

type ParamValue = string | number;

// パスパラメータは Params が宣言されていればその型、なければパスから決まるキーで string | number
type ParamsOption<P extends string, D> = [ParamNames<P>] extends [never]
  ? { params?: never }
  : D extends { params: infer X }
    ? { params: X }
    : { params: Record<ParamNames<P>, ParamValue> };

// 宣言された Params のキーがパスのパラメータと過不足なく一致し、値が string | number か
type IsValidParams<P extends string, X> = [
  Exclude<keyof X, ParamNames<P>>,
] extends [never]
  ? [Exclude<ParamNames<P>, keyof X>] extends [never]
    ? X extends Record<ParamNames<P>, ParamValue>
      ? true
      : false
    : false
  : false;

/**
 * 宣言された Params が正しくないrouteのパス
 */
export type InvalidParamsRoutes<Defs extends RouteDefinitionMap> = {
  [P in keyof Defs & string]: Defs[P] extends { params: infer X }
    ? IsValidParams<P, X> extends true
      ? never
      : P
    : never;
}[keyof Defs & string];

/**
 * never でなければ型エラーにする（src/utils/$route-client.ts でrouteの定義の検査に使う）
 */
export type AssertNever<T extends never> = T;

// 全てのプロパティがoptionalなクエリは省略できる
type QueryOption<I> = I extends { Query: infer Q }
  ? object extends Q
    ? { query?: Q }
    : { query: Q }
  : { query?: never };

type BodyOption<I> = I extends { Body: infer B }
  ? { body: B }
  : { body?: never };

export type FetchRouteOptions<
  Defs extends RouteDefinitionMap,
  M extends HttpMethod,
  P extends keyof Defs & string,
> = ParamsOption<P, Defs[P]> &
  QueryOption<MethodInput<Defs, P, M>> &
  BodyOption<MethodInput<Defs, P, M>> & {
    init?: Omit<RequestInit, "method" | "body">;
  };

// 全てのオプションが省略可能な場合は引数自体を省略可能にする
type FetchRouteArgs<O> = object extends O ? [options?: O] : [options: O];

/**
 * json() の戻り値の型が付いたレスポンス
 * JSONを返さないrouteでは json() を呼べない
 */
export type TypedResponse<T> = Omit<Response, "json"> & {
  json: [T] extends [never] ? never : () => Promise<T>;
};

/**
 * Route HandlerのURLを組み立てる
 *
 * サブドメインのパスから始まる場合はサブドメインを含むURLになる（routePathと同じ）
 */
export const buildRouteUrl = (
  pathname: string,
  options: { params?: Record<string, string | number>; query?: Query },
): string => {
  const filledPath = pathname.replaceAll(/\[(.*?)]/g, (_, key: string) =>
    encodeURIComponent(String(options.params?.[key])),
  );

  const searchParams = new URLSearchParams(
    Object.entries(options.query ?? {}).flatMap(([key, value]) =>
      (Array.isArray(value) ? value : [value])
        .filter((v) => v != null)
        .map((v) => [key, String(v)]),
    ),
  );
  const search = searchParams.size > 0 ? `?${searchParams.toString()}` : "";

  return appUrl(`${filledPath}${search}`);
};

// そのまま送信できるボディ（それ以外はJSONとして送信する）
const isRawBody = (body: unknown): body is FormData | URLSearchParams | Blob =>
  body instanceof FormData ||
  body instanceof URLSearchParams ||
  body instanceof Blob;

const buildRequestInit = (
  method: HttpMethod,
  body: unknown,
  init: RequestInit | undefined,
): RequestInit => {
  if (body === undefined) {
    return { ...init, method };
  }
  if (isRawBody(body)) {
    return { ...init, method, body };
  }

  const headers = new Headers(init?.headers);
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return { ...init, method, headers, body: JSON.stringify(body) };
};

/**
 * 指定したrouteの定義に対する fetchRoute を作る
 *
 * 通常は fetchRoute を使う（テストで任意のrouteの定義を使うためのもの）
 */
export const createFetchRoute =
  <Defs extends RouteDefinitionMap>() =>
  <P extends keyof Defs & string, M extends RouteMethod<Defs, P>>(
    pathname: P,
    method: M,
    ...[options]: FetchRouteArgs<FetchRouteOptions<Defs, M, P>>
  ): Promise<TypedResponse<RouteResponseJson<Defs, P, M>>> => {
    const { params, query, body, init } = (options ?? {}) as {
      params?: Record<string, string | number>;
      query?: Query;
      body?: unknown;
      init?: RequestInit;
    };

    return fetch(
      buildRouteUrl(pathname, { params, query }),
      buildRequestInit(method, body, init),
    ) as Promise<TypedResponse<RouteResponseJson<Defs, P, M>>>;
  };

/**
 * Route Handlerを型安全に呼び出す
 *
 * - パスを先に指定し、メソッドはそのrouteの route.ts でexportされているハンドラーのものだけ指定できる
 * - パスパラメータのキーはパスから決まる。値の型は route.ts で `export type Params = ...` をexportすると指定できる
 *   （宣言しない場合は string | number）
 * - クエリは route.ts で `export type GETQuery = ...` のようにメソッドごとにexportした型で指定する
 *   （全てのプロパティがoptionalなら省略できる、宣言が無ければ指定できない）
 * - リクエストボディは route.ts で `export type POSTBody = ...` のようにメソッドごとにexportした型で指定する
 *   （FormData / URLSearchParams / Blob はそのまま、それ以外はJSONとして送信する）
 * - レスポンスのJSONの型はハンドラーの戻り値の型から決まる（RouteHandlerResponse で宣言すると保証される）
 *
 * 型定義は `npm run gen:route-client` で src/utils/$route-client.ts に生成される
 * クライアントコンポーネントからは useRouteHandler / useRouteHandlerMutation も使える
 *
 * @example
 * const res = await fetchRoute("/admin/list/applications/shussanteatekin/[applicationId]/download", "GET", {
 *   params: { applicationId },
 * });
 *
 * @knipignore
 */
export const fetchRoute = createFetchRoute<RouteDefinitions>();
